import React, { useState, useEffect } from 'react';
import { Typography, Button, Spinner, Alert, IconButton } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { brandLogo as logo, brandName, isMaisterchef } from '../utils/branding';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';

const CancelSubscription = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('confirming'); // 'confirming', 'processing', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const { language } = useLanguage();

    // Get sessionId from URL parameter 's' first, then fallback to localStorage
    const getSessionIdFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get('s');
    };

    // Map befr to french and benl to nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    // Map all other Arabic country variants to arabic/english
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english']; // Fallback to English if translation not found

    // Terms and conditions links based on language
    const getTermsLink = () => {
        if (language === 'portuguese') return "https://souschaif.com/pt/termos-e-condicoes/";
        if (language === 'befr' || language === 'be-fr') return "https://souschaif.com/frbe/conditions-generales-2/";
        if (language === 'benl' || language === 'be-nl') return "http://souschaif.com/nl/algemene-voorwaarden/";
        if (language === 'deutsch') {
            return isMaisterchef
                ? "https://maisterchef.com/dede/allgemeine-geschaftsbedingungen-3/"
                : "https://souschaif.com/de/impressum-3/";
        }
        return "https://souschaif.com/pt/termos-e-condicoes/"; // Default to Portuguese
    };

    const termsLink = getTermsLink();

    const handleConfirmCancel = async () => {
        setStatus('processing');
        setShowAlert(false); // Hide previous alerts
        
        // Priority: URL parameter 's' > localStorage 'accountSessionId' > localStorage 'sessionId'
        const sessionIdFromUrl = getSessionIdFromUrl();
        const accountSessionId = sessionIdFromUrl || localStorage.getItem('accountSessionId') || localStorage.getItem('sessionId');
        const userId = localStorage.getItem('userId');
        const username = localStorage.getItem('username');

        console.log('CancelSubscription - sessionId sources:', {
            fromUrl: sessionIdFromUrl,
            fromAccountSessionId: localStorage.getItem('accountSessionId'),
            fromSessionId: localStorage.getItem('sessionId'),
            using: accountSessionId
        });

        if (!accountSessionId) {
            setErrorMessage(t.sessionIdNotFound || 'Erro: ID da sessão não encontrado. Por favor, faça login novamente.');
            setStatus('error');
            setShowAlert(true);
            return;
        }

        try {
            // Step 1: Call the external API to terminate the subscription
            const url = `https://signup.souschaif.com/msp/subscription/terminate?sessionId=${accountSessionId}&apiCode=souschaif`;
            console.log('Calling unsubscribe URL:', url);
            const response = await axios.get(url);
            console.log('External cancellation response:', response.data);

            // Check if a redirect URL is provided in the response
            const redirectURL = response.data?.redirectURL || response.data?.redirectUrl || response.data?.redirect_url;
            if (redirectURL) {
                console.log('Redirect URL found, navigating to:', redirectURL);
                window.location.href = redirectURL;
                return;
            }

            console.log('External cancellation successful.');

            // Step 2: Update the local database status to 'unsubscribed'
            if (userId) {
                try {
                    console.log('Updating subscription status in database for user:', userId);
                    await axios.put(`/api/user/${userId}/subscription-status`, {
                        status: 'unsubscribed'
                    });
                    console.log('Database status updated to unsubscribed.');
                } catch (dbError) {
                    console.error('Error updating subscription status in database:', dbError);
                }
            } else {
                console.warn('No userId found in localStorage, skipping database update.');
            }

            setStatus('success');
        } catch (error) {
            console.error('Error during cancellation:', error);
            setErrorMessage(t.cancellationError || 'Erro ao cancelar a assinatura. Por favor, tente novamente ou entre em contato com o suporte ao cliente.');
            setStatus('error');
            setShowAlert(true);
        }
    };

    // Render based on status
    const renderContent = () => {
        switch (status) {
            case 'processing':
                return (
                    <div className="text-center">
                        <Spinner className="h-12 w-12 mx-auto mb-4" />
                        <Typography>{t.processingCancellation || 'Processando cancelamento...'}</Typography>
                    </div>
                );
            case 'success':
                return (
                    <>
                        <Typography variant="h5" className="mb-3 font-semibold">
                            {t.cancellationComplete || 'Cancelamento Concluído'}
                        </Typography>
                        <Typography variant="paragraph" className="mb-4">
                            {t.cancellationSuccessMessage || 'Sua assinatura foi cancelada com sucesso! Você não receberá mais cobranças. Para mais informações: 707450201'}
                        </Typography>
                        <Button color="blue" className="w-full" onClick={() => navigate('/')}>
                            {t.back || 'Voltar'}
                        </Button>
                    </>
                );
            case 'error':
            // Confirmation state also handles error display via Alert
            case 'confirming':
            default:
                return (
                    <>
                        <Typography variant="h5" className="mb-3 font-semibold">
                            {t.wantToCancelService || 'Deseja cancelar o serviço?'}
                        </Typography>
                        <Typography variant="small" className="mb-4">
                            {t.clickHereToCancel || 'Você pode fazê-lo clicando aqui:'}
                        </Typography>
                        <Button color="blue" className="w-full mb-2" onClick={handleConfirmCancel}>
                            {t.cancelSubscription || 'Cancelar assinatura'}
                        </Button>
                        <Button variant="outlined" color="blue-gray" className="w-full mb-4" onClick={() => navigate('/')}>
                            {t.continueSubscription || 'Continuar assinatura'}
                        </Button>
                        <div className="text-xs text-gray-600 border-t border-gray-200 pt-3">
                            <Typography variant="small" className="mb-2">{t.contactUsVia || 'Ou pode contactar-nos através de:'}</Typography>
                            <Typography variant="small" className="mb-1">
                                Email: <a href="mailto:help@clickzillas.com" className="underline hover:text-blue-800">help@clickzillas.com</a>
                            </Typography>
                            <Typography variant="small" className="mb-3">
                                {t.customerSupportLine || 'Linha de Apoio ao Cliente'}: 707 450 201
                            </Typography>
                            <Typography variant="small" className="mb-3 italic">
                                {t.cancellationNoExtraCost || 'O cancelamento do serviço é feito sem qualquer custo adicional para o utilizador.'}
                            </Typography>
                            <Typography variant="small">
                                <a href={termsLink} className="underline hover:text-blue-800">{t.termsAndConditions || 'Termos e Condições'}</a>
                            </Typography>
                        </div>
                    </>
                );
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 text-gray-800">
            <div className="w-full max-w-sm bg-white p-6 rounded-lg shadow-md text-center">
                <img src={logo} alt={`${brandName} Logo`} className="h-16 mx-auto mb-4" />
                {renderContent()}
            </div>
            {/* Alert for errors */}
            {showAlert && status === 'error' && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                    <Alert
                        open={showAlert}
                        color="red"
                        className="flex justify-between items-center"
                        animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                        action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                    >
                        {errorMessage}
                    </Alert>
                </div>
            )}
        </div>
    );
};

export default CancelSubscription;
